
import React, { useState, useRef, useEffect } from 'react';
import { useImageOptimization } from '@/hooks/useImageOptimization';
import { Skeleton } from '@/components/ui/skeleton';

interface PerformanceImageProps extends React.ImgHTMLAttributes<HTMLImageElement> {
  src: string;
  alt: string;
  priority?: boolean;
  className?: string;
  fallbackClassName?: string;
  onLoad?: () => void;
  onError?: () => void;
  quality?: number;
  maxWidth?: number;
}

export const PerformanceImage: React.FC<PerformanceImageProps> = ({
  src,
  alt,
  priority = false,
  className = '',
  fallbackClassName = '',
  onLoad,
  onError,
  quality = 85,
  maxWidth,
  ...props
}) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isInView, setIsInView] = useState(priority);
  const imgRef = useRef<HTMLImageElement>(null);
  const { optimizeImageUrl, isMobile } = useImageOptimization();

  // Intersection Observer pour le lazy loading optimisé mobile
  useEffect(() => {
    if (priority || isInView) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      {
        rootMargin: isMobile ? '200px' : '100px',
        threshold: 0.1
      }
    );

    if (imgRef.current) {
      observer.observe(imgRef.current);
    }

    return () => observer.disconnect();
  }, [priority, isInView, isMobile]);

  const optimizedSrc = optimizeImageUrl(src, { quality, maxWidth, priority });

  const handleLoad = () => {
    setIsLoaded(true);
    setHasError(false);
    onLoad?.();
  };

  const handleError = () => {
    setHasError(true);
    onError?.();
  };

  return (
    <div ref={imgRef} className={`relative ${className}`}>
      {!isLoaded && isInView && !hasError && (
        <Skeleton className={`absolute inset-0 ${fallbackClassName}`} />
      )}
      
      {hasError ? (
        <div className={`flex items-center justify-center bg-gray-200 text-gray-500 text-sm p-4 ${fallbackClassName}`}>
          Image non disponible
        </div>
      ) : (
        isInView && (
          <img
            src={optimizedSrc}
            alt={alt}
            className={`transition-opacity duration-300 ${
              isLoaded ? 'opacity-100' : 'opacity-0'
            } ${className}`}
            loading={priority ? 'eager' : 'lazy'}
            decoding="async"
            fetchPriority={priority ? 'high' : 'auto'}
            onLoad={handleLoad}
            onError={handleError}
            {...props}
          />
        )
      )}
    </div>
  );
};
